import { ASTNode, GraphQLError as GQLError } from 'graphql';
import compress from 'graphql-query-compress';
import { print } from 'graphql/language/printer';
import retry, { AbortError } from 'p-retry';

import { Omit, SupportedLanguages } from '@rbi-ctg/frontend';
import { getGraphqlApiUrl } from 'remote/constants';
import { NetworkConnectionError } from 'remote/exceptions';
import { useNetworkContext } from 'state/network';
import { StatusType, dataDogLogger } from 'utils/datadog';
import { platform } from 'utils/environment';
import LogrocketHelper from 'utils/logrocket';

import { INetworkStatus } from './status';

// Chrome starts truncating urls at around 7500 so
// we put a max limit on it. Sanity supports up to 11KB
const MAX_QUERY_SIZE = 7500;

export class GraphQLError extends Error {
  originalError: GQLError[];

  constructor(errors: GQLError[]) {
    super(`GraphQLError occurred: ${errors.map(({ message }) => message).join('\n')}`);
    this.originalError = errors;
  }
}

export function isSanityGraphqlUrl(url: string): Boolean {
  return url.includes('sanity.io') && url.includes('graphql');
}

export function isLambdaUrl(url: string): Boolean {
  return url === getGraphqlApiUrl();
}

export type FetchConfig<R, V, C = never> = Omit<Parameters<GlobalFetch['fetch']>[1], 'headers'> & {
  body?: string;
  /**
   * Because typescript has no notion of throwing,
   * we can use a default return type because the assumption
   * is that every catch function will either throw an error
   * or return an object of the correct type. The third type
   * argument to FetchConfig can be used to provide a
   * "failure" or "left" type if this is not the case.
   */
  catch?: (err: Error) => C;
  connection?: INetworkStatus;
  headers?: {
    [header: string]: string;
  };
  language?: SupportedLanguages;
  maxRetries?: number;
  method?: string;
  query?: ASTNode;
  sessionId?: string;
  region?: string;
  then: (res: object) => R;
  variables?: V;
};

function getQs(query: string, variables: object) {
  const baseQs = `?query=${encodeURIComponent(query)}`;
  const varsQs = variables ? `&variables=${encodeURIComponent(JSON.stringify(variables))}` : '';
  return `${baseQs}${varsQs}`;
}

export const useSanityGqlEndpoint = () => useNetworkContext().sanityEndpoints.graphql;

export function fetch<R, V, C = never>(URL: string, config: FetchConfig<R, V, C>): Promise<R | C> {
  const {
    body,
    connection,
    headers,
    language = 'en',
    maxRetries = 0,
    method = 'POST',
    query: queryAst,
    then,
  } = config;
  const logrocketInstance = LogrocketHelper.getInstance().logrocket;

  // localize all queries
  const query = queryAst
    ? compress(
        print(queryAst)
          // Replaces _LANG_ with `en` if language is english
          .replace(/_LANG_/g, language)
      )
    : '';

  const encodedQueryString = getQs(query, config.variables || {});

  const requestOpts: RequestInit = {
    headers,
    method: method === 'GET' ? 'GET' : 'POST',
  };

  if (isSanityGraphqlUrl(URL)) {
    // Wipe all headers when hitting the sanity CDN
    delete requestOpts.headers;

    if (encodedQueryString.length < MAX_QUERY_SIZE) {
      URL += encodedQueryString;

      requestOpts.method = 'GET';
    } else {
      requestOpts.method = 'POST';

      requestOpts.headers = { 'Content-Type': 'application/json' };
    }

    requestOpts.credentials = 'omit';
    requestOpts.referrerPolicy = 'no-referrer';
  }

  // add sessionId header if fetch to our graphql
  if (isLambdaUrl(URL)) {
    requestOpts.headers = requestOpts.headers || {};
    requestOpts.headers['X-Session-Id'] = config.sessionId;

    if (logrocketInstance) {
      requestOpts.headers['x-lr-session-url'] = logrocketInstance.sessionURL;
    }

    if (config.language) {
      requestOpts.headers['x-ui-language'] = config.language;
    }

    if (config.region) {
      requestOpts.headers['x-ui-region'] = config.region;
    }

    requestOpts.headers['x-ui-platform'] = platform();
  }

  if (requestOpts.method === 'POST') {
    requestOpts.body =
      body ||
      JSON.stringify({
        query,
        variables: config.variables || {},
      });
  }

  const run = () =>
    window
      .fetch(URL, requestOpts)
      .then(res => {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.indexOf('application/json') === -1) {
          const errorRes: Promise<any> = res.text().then(responseText => {
            const err = new Error(`[NetworkResponseNotOk]: Url: ${URL}`);
            dataDogLogger({
              message: `${err}`,
              context: {
                responseText,
              },
              status: StatusType.error,
            });

            throw new NetworkConnectionError();
          });

          return errorRes;
        }

        if (res.status === 404) {
          throw new AbortError(res.statusText);
        }

        return res.json();
      })
      .then(res => {
        if (res.errors) {
          throw new AbortError(new GraphQLError(res.errors));
        }
        if (res.data) {
          return res.data;
        }

        return res;
      })
      .then(then)
      .catch(err => {
        dataDogLogger({ message: `${err}`, context: { URL }, status: StatusType.error });

        const noInternetErrors = [
          'Failed to fetch',
          'Network request failed',
          'NetworkError when attempting to fetch resource.',
          'The Internet connection appears to be offline',
        ];

        if (err instanceof AbortError) {
          throw err;
        }
        if (
          (connection && !connection.connected) ||
          (err instanceof TypeError && noInternetErrors.includes(err.message))
        ) {
          throw new NetworkConnectionError();
        }
        if (config.catch) {
          return config.catch(err);
        }
        throw err;
      });

  return retry(run, { retries: maxRetries });
}
